/**
 * Service category tabs for the "What we're booked for most" band.
 *
 * The panels are all server-rendered; this only decides which one is visible,
 * so the band still lists every category with JS off (each panel renders, they
 * are simply all shown). Nothing is fetched and nothing is re-ordered.
 *
 * Markup contract (see components/home/popular_services_v3.html):
 *   [data-service-tabs]                 the tablist
 *     button[role=tab][aria-controls]   one per category, aria-controls the panel id
 *   [role=tabpanel]                     one per category, hidden when not current
 *
 * The selected tab carries `is-active`, which the template styles with Tailwind
 * arbitrary variants — rename one without the other and the tab stops looking
 * selected while still working.
 */
class ServiceTabs {
    constructor(tablist) {
        this.tablist = tablist;
        this.tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
        if (this.tabs.length < 2) return;

        this.tabs.forEach((tab, index) => {
            tab.addEventListener('click', () => this.select(index));
            tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
        });

        const current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
        this.select(current === -1 ? 0 : current, { focus: false });
    }

    panelFor(tab) {
        const id = tab.getAttribute('aria-controls');
        return id ? document.getElementById(id) : null;
    }

    select(index, { focus = true } = {}) {
        this.tabs.forEach((tab, i) => {
            const isCurrent = i === index;
            tab.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
            tab.setAttribute('tabindex', isCurrent ? '0' : '-1');
            tab.classList.toggle('is-active', isCurrent);

            const panel = this.panelFor(tab);
            if (panel) panel.hidden = !isCurrent;
        });

        if (focus) this.tabs[index].focus();
    }

    /** Left/right move between tabs, home/end jump to the ends — the WAI-ARIA tabs pattern. */
    onKeydown(event, index) {
        const last = this.tabs.length - 1;
        let next = null;

        if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
        else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = last;

        if (next === null) return;
        event.preventDefault();
        this.select(next);
    }
}

function initServiceTabs() {
    document.querySelectorAll('[data-service-tabs]').forEach((tablist) => new ServiceTabs(tablist));
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initServiceTabs);
} else {
    initServiceTabs();
}
